import { useCallback, useEffect, useRef } from 'react'

type UseTableFocusManagementProps = {
    totalRowsOnPage: number
    page: number
}

type UseTableFocusManagementReturn = {
    focusedRowIndexRef: React.MutableRefObject<number>
    focusAnchorRefs: React.MutableRefObject<Map<number, HTMLButtonElement>>
    updateFocusedRow: (newIndex: number) => void
    handleTableFocus: (e: React.FocusEvent<HTMLTableSectionElement>) => void
}

export const useTableFocusManagement = ({
    totalRowsOnPage,
    page,
}: UseTableFocusManagementProps): UseTableFocusManagementReturn => {
    const focusAnchorRefs = useRef<Map<number, HTMLButtonElement>>(new Map())
    const focusedRowIndexRef = useRef<number>(0)

    const updateFocusedRow = useCallback((newIndex: number): void => {
        const clampedIndex = Math.max(0, Math.min(newIndex, totalRowsOnPage - 1))
        const oldIndex = focusedRowIndexRef.current
        if (oldIndex === clampedIndex) return

        const oldAnchor = focusAnchorRefs.current.get(oldIndex)
        if (oldAnchor) oldAnchor.tabIndex = -1

        const newAnchor = focusAnchorRefs.current.get(clampedIndex)
        if (newAnchor) {
            newAnchor.tabIndex = 0
            newAnchor.focus()
        }

        focusedRowIndexRef.current = clampedIndex
    }, [totalRowsOnPage])

    // Reset focused row index when page changes
    useEffect(() => {
        if (totalRowsOnPage > 0) {
            updateFocusedRow(0)
        } else {
            focusedRowIndexRef.current = 0
        }
    }, [page, totalRowsOnPage, updateFocusedRow])

    const handleTableFocus = useCallback((e: React.FocusEvent<HTMLTableSectionElement>): void => {
        const target = e.target as HTMLElement
        const row = target.closest('[data-row-index]')
        if (row) {
            const rowIndex = parseInt(row.getAttribute('data-row-index') || '0', 10)
            if (focusedRowIndexRef.current !== rowIndex) {
                const oldAnchor = focusAnchorRefs.current.get(focusedRowIndexRef.current)
                if (oldAnchor) oldAnchor.tabIndex = -1

                const newAnchor = focusAnchorRefs.current.get(rowIndex)
                if (newAnchor) newAnchor.tabIndex = 0

                focusedRowIndexRef.current = rowIndex
            }
        }
    }, [])

    return {
        focusedRowIndexRef,
        focusAnchorRefs,
        updateFocusedRow,
        handleTableFocus,
    }
}
